import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Tooltip from "../../../ui/Tooltip";
import type { AppCategory, AppItem } from "../types";
import { startAppHoverDwell, endAppHoverDwell } from "../sidebarAnalytics";
import { useFavoriteApps } from "../../AppsRail/useFavoriteApps";
import { MAX_FAVORITES } from "../../AppsRail/favoriteApps";
import { appTileClass } from "../appTile";

// Menú de apps (Daniel, 30-oct, referencia: el «Menú» de Facebook): buscador
// arriba, cada categoría como sección abierta y cada app en una fila con su
// descripción. Antes era un cajón a todo lo alto, con categorías plegadas y
// cuadros de 100 px cuya descripción sólo salía al pasar el cursor.

interface AppsMenuProps {
  categories: AppCategory[];
  selectedAppId?: string;
  onAppClick: (app: AppItem, categoryId: string, categoryTitle: string) => void;
  isDarkMode: boolean;
  twoColumns?: boolean;
}

// "geocercas" encuentra "Geocércas" y viceversa.
const fold = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

export function AppsMenu({ categories, selectedAppId, onAppClick, isDarkMode, twoColumns = true }: AppsMenuProps) {
  const { t } = useTranslation("common");
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const needle = fold(query.trim());
    if (!needle) return categories;
    return categories
      .map((c) => ({ ...c, apps: c.apps.filter((a) => fold(`${a.name} ${a.description ?? ""}`).includes(needle)) }))
      .filter((c) => c.apps.length > 0);
  }, [categories, query]);

  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div>
      <label className={`mx-1.5 mb-1 flex items-center gap-2 h-9 px-3 rounded-full ${isDarkMode ? "bg-white/[0.07]" : "bg-black/[0.045]"}`}>
        <i className={`ri-search-line text-[15px] ${muted}`} aria-hidden />
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("appsSidebar.search", "Busca una app")}
          aria-label={t("appsSidebar.search", "Busca una app")}
          className={`flex-1 min-w-0 bg-transparent text-[14px] outline-none ${
            isDarkMode ? "text-gray-100 placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-500"
          }`}
        />
      </label>

      {shown.length === 0 ? (
        <p className={`px-3 py-8 text-center text-sm ${muted}`}>{t("appsSidebar.noResults", "Ninguna app coincide")}</p>
      ) : (
        <div className={twoColumns ? "sm:columns-2 sm:gap-x-2" : ""}>
          {shown.map((category) => (
            <section key={category.id} className="break-inside-avoid pt-2">
              <h3 className={`px-2.5 pt-1 pb-1.5 text-[15px] font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                {category.title}
              </h3>
              {category.apps.map((app) => (
                <AppRow
                  key={app.id}
                  app={app}
                  categoryId={category.id}
                  isSelected={selectedAppId === app.id}
                  onClick={() => onAppClick(app, category.id, category.title)}
                  isDarkMode={isDarkMode}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

interface AppRowProps {
  app: AppItem;
  categoryId: string;
  isSelected: boolean;
  onClick: () => void;
  isDarkMode: boolean;
}

function AppRow({ app, categoryId, isSelected, onClick, isDarkMode }: AppRowProps) {
  const { t } = useTranslation("common");
  const off = !!(app.disabled || app.isComingSoon);
  const tag = app.isComingSoon ? t("appsRail.reports.soon", "Pronto") : app.isBeta ? "Beta" : app.badge;

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => startAppHoverDwell(app.id, categoryId)}
        onMouseLeave={() => endAppHoverDwell(app.id, categoryId)}
        disabled={off}
        aria-current={isSelected ? "page" : undefined}
        className={`w-full flex items-start gap-3 p-2 pr-9 rounded-xl text-left transition-[background-color,transform] duration-150 ease-out active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${
          isSelected
            ? isDarkMode ? "bg-white/[0.06]" : "bg-black/[0.04]"
            : off ? "" : isDarkMode ? "hover:bg-white/[0.06]" : "hover:bg-black/[0.04]"
        }`}
      >
        <span className={`size-9 shrink-0 grid place-items-center rounded-[10px] text-lg transition-colors duration-150 ${appTileClass(off ? "disabled" : isSelected ? "active" : "idle", isDarkMode)}`}>{app.icon}</span>
        <span className="min-w-0 flex-1 pt-px">
          <span className="flex items-center gap-1.5">
            <span className={`text-[14px] font-medium truncate ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>{app.name}</span>
            {tag && (
              <span
                className={`shrink-0 px-1.5 rounded-md border text-[10px] font-semibold leading-4 ${
                  isDarkMode ? "border-white/15 text-gray-400" : "border-black/10 text-gray-500"
                }`}
              >
                {tag}
              </span>
            )}
          </span>
          {app.description && (
            <span className={`block text-xs leading-snug line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {app.description}
            </span>
          )}
        </span>
      </button>
      {!off && <FavoriteStar app={app} />}
    </div>
  );
}

// La estrella sola no se entiende para todos: buena parte de quienes usan la
// plataforma no reconoce el ícono como "favorito". Cada toque responde con un
// aviso ESCRITO que dice qué pasó y dónde verlo; la animación sólo acompaña.
// Es un botón hermano de la fila, no hijo: un botón dentro de otro es HTML
// inválido y rompe el foco con teclado.
function FavoriteStar({ app }: { app: AppItem }) {
  const { t } = useTranslation("common");
  const { favorites, isFavorite, toggle } = useFavoriteApps();
  const fav = isFavorite(app.id);
  const label = fav
    ? t("appsRail.unpin", "Quitar de mi barra de accesos rápidos")
    : t("appsRail.pin", "Agregar a mi barra de accesos rápidos");
  // Cambia en cada toque para reiniciar la animación de la estrella.
  const [popKey, setPopKey] = useState(0);

  const onStar = () => {
    if (!fav && favorites.length >= MAX_FAVORITES) {
      toast.info(t("appsRail.full", { max: MAX_FAVORITES, defaultValue: "Tu barra de accesos rápidos ya tiene {{max}} apps. Quita una con su estrella para agregar otra." }));
      return;
    }
    toggle(app.id);
    setPopKey((k) => k + 1);
    if (fav) toast.info(t("appsRail.removed", { name: app.name, defaultValue: "{{name}} se quitó de tu barra de accesos rápidos." }));
    else toast.success(t("appsRail.added", { name: app.name, defaultValue: "{{name}} se agregó a tu barra de accesos rápidos, a la izquierda de la pantalla." }));
  };

  return (
    <div className="absolute top-2 right-1.5">
      <Tooltip content={label} delay={150} position="left" className="!w-auto">
        <button
          type="button"
          aria-pressed={fav}
          aria-label={`${label}: ${app.name}`}
          onClick={onStar}
          className={`relative size-7 grid place-items-center rounded-lg transition-opacity focus-visible:opacity-100 ${
            fav ? "opacity-100 text-amber-400" : "opacity-0 group-hover:opacity-100 text-gray-400 hover:text-amber-400"
          }`}
        >
          {popKey > 0 && fav && (
            <span key={`burst-${popKey}`} aria-hidden className="absolute inset-0 rounded-full bg-amber-400/60 motion-safe:animate-starBurst opacity-0" />
          )}
          <i
            key={`star-${popKey}`}
            className={`${fav ? "ri-star-fill" : "ri-star-line"} relative text-sm leading-none ${popKey > 0 ? "motion-safe:animate-starPop" : ""}`}
          />
        </button>
      </Tooltip>
    </div>
  );
}
